import { CycleUtils } from '@wix/package-picker-shared';
import { SwitchProductModule } from '@wix/package-picker-sdk';
import {
  useCycleAvailability,
  useCycleName,
} from '@wix/package-picker-shared-eclipse';
import { PurchaseOffering } from '@wix/premium-purchase-platform/@sdk';
import { EclipsePackagePickerModule } from '../../modules/eclipse-package-picker.module';

interface UseCycleSelectorItemTooltipProps {
  cycle?: PurchaseOffering.PurchaseCycle;
  productId: string;
}

export const useCycleSelectorItemTooltip = ({
  cycle,
  productId,
}: UseCycleSelectorItemTooltipProps) => {
  const { t } = EclipsePackagePickerModule.internals.useTranslations();
  const currentProductQuery =
    SwitchProductModule.SDK.switchApiClient.hooks.useGetCurrentProduct({});
  const isCurrentProduct =
    currentProductQuery.data?.currentProduct?.productId === productId;
  const currentCycle = currentProductQuery.data?.currentProduct?.cycle;

  const { data: product, isLoading: isProductLoading } =
    EclipsePackagePickerModule.SDK.eclipseOfferingApiClient.hooks.useFetchProduct(
      { productId },
      Boolean(productId),
    );

  const { isCycleAvailable } = useCycleAvailability(
    productId,
    cycle || undefined,
  );

  const currentCycleName = useCycleName({ cycle: currentCycle });

  if (!cycle || currentProductQuery.isLoading || isProductLoading) {
    return null;
  }

  const getTooltipKey = () => {
    if (isCycleAvailable || !currentCycleName) {
      return null;
    }

    const maxPossibleCycle = CycleUtils.maxCycle(
      product?.pricingDetails?.map((p) => p.cycle) ?? [],
    );

    if (CycleUtils.isCycleEqual(currentCycle, maxPossibleCycle)) {
      return 'Advanced_plans.second_upgrade.cycle.upgrade_max_cycle.disabled_tooltip';
    }

    if (isCurrentProduct) {
      return 'Advanced_plans.second_upgrade.cycle.extend.disabled_tooltip';
    } else {
      return 'Advanced_plans.second_upgrade.cycle.upgrade.disabled_tooltip';
    }
  };

  const tooltipKey = getTooltipKey();
  const tooltipContent = t(tooltipKey ?? '', {
    currentCycle: currentCycleName,
  });

  const onShowTooltip = () => {
    EclipsePackagePickerModule.internals.bi.onCycleSelectorTooltipShow?.({
      cycle,
      key: tooltipKey,
    });
  };

  return {
    tooltipKey,
    tooltipContent,
    onShowTooltip,
  };
};
